import { INVENTORY_REASONS, INVENTORY_STATUSES, type InventoryDetail } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { INVENTORY_TONE, inventoryQuery, itemsQuery, parseNum, qty } from './queries';

/** Ведомость инвентаризации: пересчёт, расхождения, утверждение руководителем проекта (СК-26) */
export function InventoryPage() {
  const { inventoryId } = useParams({ strict: false }) as { inventoryId: string };
  const qc = useQueryClient();
  const q = useQuery(inventoryQuery(inventoryId));
  const d = q.data;
  useCrumbs(
    d
      ? [
          { label: 'Инвентаризация', to: '/warehouse?tab=inventories' },
          { label: d.number, title: d.warehouseName },
        ]
      : null,
    q.error,
  );
  const [vals, setVals] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const refresh = (x: InventoryDetail) => {
    qc.setQueryData(inventoryQuery(inventoryId).queryKey, x);
    qc.invalidateQueries({ queryKey: ['wh'] });
  };
  // Хранятся только правки человека: строка, записанная с телефона, не затирается ведомостью на компьютере
  const shownVal = (l: InventoryDetail['lineList'][number]) =>
    vals[l.id] ?? (l.countedQty === null ? '' : String(l.countedQty));
  const shownNote = (l: InventoryDetail['lineList'][number]) => notes[l.id] ?? l.note ?? '';
  const edited = (l: InventoryDetail['lineList'][number]) =>
    (vals[l.id] !== undefined && vals[l.id] !== (l.countedQty === null ? '' : String(l.countedQty))) ||
    (notes[l.id] !== undefined && notes[l.id] !== (l.note ?? ''));
  const save = useMutation({
    mutationFn: () =>
      api<InventoryDetail>(`/warehouse/inventories/${inventoryId}/count`, {
        body: {
          lines: (d?.lineList ?? []).filter(edited).map((l) => ({
            lineId: l.id,
            countedQty: shownVal(l).trim() === '' ? null : parseNum(shownVal(l)),
            note: shownNote(l).trim() || null,
          })),
        },
      }),
    onSuccess: (x) => {
      refresh(x);
      setVals({});
      setNotes({});
      toast('Пересчёт сохранён');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const act = useMutation({
    mutationFn: (v: { path: string; body?: unknown }) =>
      api<InventoryDetail>(`/warehouse/inventories/${inventoryId}/${v.path}`, { body: v.body ?? {} }),
    onSuccess: (x, v) => {
      refresh(x);
      setCancelling(false);
      setApproving(false);
      toast(
        v.path === 'submit'
          ? 'Ведомость отправлена руководителю проекта'
          : v.path === 'approve'
            ? 'Ведомость утверждена, корректировки проведены'
            : 'Инвентаризация отменена',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !d)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const dirty = d.lineList.some(edited);
  const diffs = d.lineList.filter((l) => l.diff !== null && Math.abs(l.diff) > 1e-9);
  return (
    <Page>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {d.number}
            <Badge tone={INVENTORY_TONE[d.status]}>{INVENTORY_STATUSES[d.status]}</Badge>
          </span>
        }
        subtitle={`${d.warehouseName} · ${INVENTORY_REASONS[d.reason]} · снимок учёта ${dateTime(d.startedAt)}`}
        actions={
          <>
            {d.can.count ? (
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={!dirty}
                onClick={() => save.mutate()}
              >
                Сохранить пересчёт
              </Button>
            ) : null}
            {d.can.count ? (
              <Button
                disabled={dirty}
                title={dirty ? 'Сначала сохраните пересчёт' : undefined}
                onClick={() => setAdding(true)}
              >
                Нашлась позиция
              </Button>
            ) : null}
            {d.can.submit ? (
              <Button
                disabled={dirty || !d.counted}
                loading={act.isPending && act.variables?.path === 'submit'}
                onClick={() => act.mutate({ path: 'submit' })}
              >
                Ведомость на утверждение
              </Button>
            ) : null}
            {d.can.approve ? (
              <Button variant="primary" onClick={() => setApproving(true)}>
                Утвердить ведомость
              </Button>
            ) : null}
            {d.can.cancel ? (
              <Button variant="danger" onClick={() => setCancelling(true)}>
                Отменить
              </Button>
            ) : null}
            <HistoryButton table="inventories" rowId={d.id} title={d.number} />
          </>
        }
      />
      <TitleBlock
        className="mb-6"
        items={[
          { label: 'Позиций в снимке', value: d.lines },
          { label: 'Пересчитано', value: d.counted, note: 'пустое поле — не проверено, а не ноль' },
          { label: 'С расхождением', value: d.withDiff, tone: d.withDiff ? 'danger' : 'success' },
        ]}
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
        <Panel title="Ведомость" meta="ожидаемое = снимок + движения после начала" bodyClassName="p-0">
          {d.lineList.length ? (
            <>
              <ul className="divide-y divide-line md:hidden">
                {d.lineList.map((l) => (
                  <li key={l.id} className="flex flex-col gap-1.5 px-4 py-3 text-[13px]">
                    <Link
                      to={`/warehouse/items/${l.itemId}`}
                      className="text-[14px] font-medium text-ink hover:underline"
                    >
                      {l.itemName}
                    </Link>
                    <span className="num text-ink-2">
                      ожидаемое {qty(l.expectedQty)} {l.unit}
                      {l.movedQty
                        ? ` (снимок ${qty(l.snapshotQty)}, движения ${l.movedQty > 0 ? '+' : ''}${qty(l.movedQty)})`
                        : ''}
                    </span>
                    {d.can.count ? (
                      <Input
                        inputMode="decimal"
                        placeholder="Пересчитано"
                        value={shownVal(l)}
                        onChange={(e) => setVals({ ...vals, [l.id]: e.target.value })}
                      />
                    ) : (
                      <span className="num">
                        пересчитано {l.countedQty === null ? 'не проверено' : qty(l.countedQty)}
                      </span>
                    )}
                    {l.diff !== null ? (
                      <span
                        className={clsx(
                          'num font-semibold',
                          Math.abs(l.diff) > 1e-9 ? 'text-danger' : 'text-success',
                        )}
                      >
                        расхождение {l.diff > 0 ? '+' : ''}
                        {qty(l.diff)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <ScrollX className="hidden md:block">
                <table className="w-full min-w-[760px] text-[14px]">
                  <thead>
                    <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                      <th className="py-2.5 pr-3 pl-5 font-medium">Позиция</th>
                      <th className="py-2.5 pr-3 text-right font-medium">Снимок</th>
                      <th className="py-2.5 pr-3 text-right font-medium">Движения</th>
                      <th className="py-2.5 pr-3 text-right font-medium">Ожидаемое</th>
                      <th className="py-2.5 pr-3 text-right font-medium">Пересчитано</th>
                      <th className="py-2.5 pr-3 text-right font-medium">Расхождение</th>
                      <th className="py-2.5 pr-5 font-medium">Примечание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lineList.map((l) => (
                      <tr key={l.id} className="border-t border-line align-top">
                        <td className="py-2.5 pr-3 pl-5">
                          <Link to={`/warehouse/items/${l.itemId}`} className="font-medium hover:underline">
                            {l.itemName}
                          </Link>
                          <div className="num text-[12px] text-ink-2">
                            {l.itemCode} · {l.unit}
                          </div>
                        </td>
                        <td className="num py-2.5 pr-3 text-right text-ink-2">{qty(l.snapshotQty)}</td>
                        <td className="num py-2.5 pr-3 text-right text-ink-2">
                          {l.movedQty ? `${l.movedQty > 0 ? '+' : ''}${qty(l.movedQty)}` : '—'}
                        </td>
                        <td className="num py-2.5 pr-3 text-right">{qty(l.expectedQty)}</td>
                        <td className="num py-2.5 pr-3 text-right">
                          {d.can.count ? (
                            <Input
                              className="h-9 w-28 text-right"
                              inputMode="decimal"
                              placeholder="—"
                              value={shownVal(l)}
                              onChange={(e) => setVals({ ...vals, [l.id]: e.target.value })}
                            />
                          ) : l.countedQty === null ? (
                            <span className="text-ink-2">не проверено</span>
                          ) : (
                            qty(l.countedQty)
                          )}
                        </td>
                        <td
                          className={clsx(
                            'num py-2.5 pr-3 text-right font-semibold',
                            l.diff !== null && Math.abs(l.diff) > 1e-9 ? 'text-danger' : 'text-ink-2',
                          )}
                        >
                          {l.diff === null ? '—' : `${l.diff > 0 ? '+' : ''}${qty(l.diff)}`}
                        </td>
                        <td className="py-2.5 pr-5 text-[13px]">
                          {d.can.count ? (
                            <Input
                              className="h-9"
                              value={shownNote(l)}
                              onChange={(e) => setNotes({ ...notes, [l.id]: e.target.value })}
                            />
                          ) : (
                            <>
                              {l.note ?? ''}
                              {l.countedByName ? (
                                <div className="text-ink-2">
                                  {l.countedByName}, {dateTime(l.countedAt)}
                                </div>
                              ) : null}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </>
          ) : (
            <div className="p-5">
              <EmptyState title="На складе по учёту пусто">
                Если при пересчёте что-то нашлось — «Нашлась позиция».
              </EmptyState>
            </div>
          )}
        </Panel>
        <div className="flex flex-col gap-6">
          <Panel title="Инвентаризация">
            <KeyValue
              items={[
                {
                  label: 'Склад',
                  value: (
                    <Link className="hover:underline" to={`/warehouse/warehouses/${d.warehouseId}`}>
                      {d.warehouseName}
                    </Link>
                  ),
                },
                { label: 'Причина', value: INVENTORY_REASONS[d.reason] },
                { label: 'Начата', value: dateTime(d.startedAt) },
                ...(d.submittedAt ? [{ label: 'Ведомость подана', value: dateTime(d.submittedAt) }] : []),
                ...(d.approvedAt
                  ? [{ label: 'Утвердил', value: `${d.approvedByName ?? '—'}, ${dateTime(d.approvedAt)}` }]
                  : []),
                ...(d.documentId
                  ? [
                      {
                        label: 'Корректировки',
                        value: (
                          <Link className="hover:underline" to={`/warehouse/documents/${d.documentId}`}>
                            {d.documentNumber}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.note
                  ? [{ label: 'Примечание', value: <span className="whitespace-pre-line">{d.note}</span> }]
                  : []),
              ]}
            />
          </Panel>
          <p className="text-[13px] text-ink-2">
            Склад работает во время пересчёта: ожидаемое количество считается на момент ввода. Пересчёт с
            телефона — по QR на этикетке: карточка позиции показывает поле ввода. Корректировки проводятся
            документом «Инвентаризация» только по проверенным строкам с расхождением.
          </p>
        </div>
      </div>
      {adding ? <AddLineDialog inventoryId={d.id} onClose={() => setAdding(false)} onDone={refresh} /> : null}
      {approving ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setApproving(false)}
          title="Утвердить ведомость расхождений"
          description={
            diffs.length
              ? `Будет проведён документ корректировок по ${diffs.length} строкам; непроверенные строки не трогаются`
              : 'Расхождений нет — документ корректировок не нужен'
          }
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setApproving(false)}>
                Отмена
              </Button>
              <Button
                variant="primary"
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'approve', body: { note: reason.trim() || null } })}
              >
                Утвердить
              </Button>
            </>
          }
        >
          <Field label="Решение руководителя проекта" hint="причины недостач и излишков, что делать дальше">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {cancelling ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setCancelling(false)}
          title={`Отменить ${d.number}`}
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setCancelling(false)}>
                Назад
              </Button>
              <Button
                variant="danger"
                loading={act.isPending}
                disabled={cancelReason.trim().length < 3}
                onClick={() => act.mutate({ path: 'cancel', body: { reason: cancelReason.trim() } })}
              >
                Отменить инвентаризацию
              </Button>
            </>
          }
        >
          <Field label="Причина">
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
}

function AddLineDialog({
  inventoryId,
  onClose,
  onDone,
}: {
  inventoryId: string;
  onClose: () => void;
  onDone: (d: InventoryDetail) => void;
}) {
  const items = useQuery(itemsQuery());
  const [itemId, setItemId] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api<InventoryDetail>(`/warehouse/inventories/${inventoryId}/lines`, { body: { itemId } }),
    onSuccess: (d) => {
      onDone(d);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Нашлась позиция, которой нет в снимке"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" disabled={!itemId} loading={m.isPending} onClick={() => m.mutate()}>
            Добавить строку
          </Button>
        </>
      }
    >
      <Field label="Позиция каталога">
        <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Выберите</option>
          {(items.data ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.code} · {x.name}
            </option>
          ))}
        </Select>
      </Field>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}
